import React from 'react';
import { Controller, type Control } from 'react-hook-form';
import { AppAdminFormLabel, AppForm, AppSelect } from '@/components/atoms';
import { Building } from '@/components/atoms/AppIcon';
import type { MassagerEditVisitFormData } from '@/schemas/massagerEditVisitSchema';

const CLINIC_LOCATION_PLACEHOLDER = 0;

interface VisitClinicLocationFieldProps {
  /** Locked visits render a read-only card with name + address; unlocked → dropdown. */
  isLocked: boolean;
  /** Locked-state name (clinic_locations.location_name). */
  lockedName: string | null;
  /** Locked-state address (concat of address1+2+3 from VisitResource). */
  lockedAddress: string | null;
  /** Dropdown options for the unlocked branch. */
  options: { value: number; label: string }[];
  control: Control<MassagerEditVisitFormData>;
  /**
   * Whether THIS field is the page-level "first error". Page owns the unified
   * banner + single-red-border logic; the molecule just renders the visual
   * state it's told to.
   */
  isFirstError: (name: keyof MassagerEditVisitFormData) => boolean;
}

/**
 * 施設 field. Two visual states:
 *  - locked    → purple read-only card (Building icon + name + address). Same
 *                shape as VisitCard's 施設 box so users see the same data
 *                whether they're on the list or the edit screen.
 *  - unlocked  → purple card with the 施設 label and an AppSelect that combines
 *                location_name + address per option.
 */
const VisitClinicLocationField: React.FC<VisitClinicLocationFieldProps> = ({
  isLocked,
  lockedName,
  lockedAddress,
  options,
  control,
  isFirstError,
}) => {
  if (isLocked) {
    if (!lockedName && !lockedAddress) return null;
    return (
      <div className="flex items-start gap-2 rounded-md border border-purple-200 bg-purple-50 px-3 py-2.5 text-purple-700">
        <span className="mt-0.5 shrink-0">
          <Building />
        </span>
        <div className="flex min-w-0 flex-col gap-0.5 text-sm">
          {lockedName && <span className="font-semibold">{lockedName}</span>}
          {lockedAddress && <span className="break-all">{lockedAddress}</span>}
        </div>
      </div>
    );
  }

  return (
    <Controller
      name="clinic_location_id"
      control={control}
      render={({ field }) => (
        <div className="rounded-md border border-purple-200 bg-purple-50 px-3 py-3">
          <div className="mb-1 flex items-center gap-2 text-purple-700">
            <Building />
            <AppAdminFormLabel label="施設" required className="!mb-0" />
          </div>
          <AppForm.Item
            validateStatus={isFirstError('clinic_location_id') ? 'error' : undefined}
            className="!mb-0"
          >
            <AppSelect
              id="visit-clinic-location"
              value={field.value ?? undefined}
              onChange={v => {
                if (v == null) {
                  field.onChange(undefined);
                  return;
                }
                const num = typeof v === 'number' ? v : Number(v);
                field.onChange(num === CLINIC_LOCATION_PLACEHOLDER ? undefined : num);
              }}
              onBlur={field.onBlur}
              options={options}
              placeholder=""
              className="w-full"
              allowClear
            />
          </AppForm.Item>
        </div>
      )}
    />
  );
};

export default VisitClinicLocationField;
export { CLINIC_LOCATION_PLACEHOLDER };
